import { useEffect, useState } from "preact/hooks";
import { App } from "../app";
import { launch, scheduleChecks, type LaunchOutcome } from "../pwa/launch";
import { startSync } from "../sync";
import { InstallScreen } from "./install-screen";

type Phase = { kind: "starting" } | { kind: "error"; message: string } | LaunchOutcome;

/**
 * The launch gate (issue #3): nothing of the app renders until the Snapshot is installed, and
 * a staged update applies (then reloads) before the first screen. The install screen cannot be
 * dismissed; the app appears when the download completes.
 *
 * Sync (#25) starts once the gate is through, so a pull never meets a Snapshot that is about to
 * be replaced, and the sign-in redirect is spent against the build that will serve it.
 */
export function Boot() {
  const [phase, setPhase] = useState<Phase>({ kind: "starting" });
  useEffect(() => {
    launch().then(setPhase, (e: unknown) => setPhase({ kind: "error", message: String(e) }));
  }, []);
  useEffect(() => {
    if (phase.kind !== "ready" && phase.kind !== "unsupported") return;
    scheduleChecks();
    void startSync();
  }, [phase.kind]);
  if (phase.kind === "starting") return <div class="boot" role="status"><p class="muted">Starting…</p></div>;
  if (phase.kind === "reloading") return <div class="boot" role="status"><p>Applying the update to {phase.to.release}…</p></div>;
  if (phase.kind === "error") return <div class="boot" role="alert"><p>The app could not start: {phase.message}</p><button type="button" class="primary" onClick={() => location.reload()}>Try again</button></div>;
  if (phase.kind === "install") return <InstallScreen onInstalled={(active) => setPhase({ kind: "ready", active, pending: null, reason: null })} />;
  return <App />;
}
